"use client";

import { useEffect } from "react";
import { AlertCircle, AlertTriangle, Check, X } from "lucide-react";
import { useToastStore } from "@/store/index";
import { Button } from "./button";

const toastTypes = ["ok", "warn", "err"] as const;
export type ToastTypes = (typeof toastTypes)[number];

const toastStyles = {
  ok: {
    bg: "bg-green-main",
    iconColor: "text-green-main",
    Icon: Check,
  },
  warn: {
    bg: "bg-yellow-500",
    iconColor: "text-yellow-500",
    Icon: AlertTriangle,
  },
  err: {
    bg: "bg-red-500",
    iconColor: "text-red-500",
    Icon: AlertCircle,
  },
};

export function Toast() {
  const {
    isToastVisible,
    toastMessage,
    toastType,
    toastExtraInfo,
    toastHideTime,
    hideToast,
  } = useToastStore();

  useEffect(() => {
    if (isToastVisible) {
      const timer = setTimeout(() => {
        hideToast();
      }, toastHideTime);
      return () => clearTimeout(timer);
    }
  }, [isToastVisible, hideToast, toastHideTime]);

  if (!isToastVisible) return null;

  const safeType = toastTypes.includes(toastType) ? toastType : "ok";
  const { bg, iconColor, Icon } = toastStyles[safeType];

  return (
    <div className="animate-slide-down fixed top-4 left-1/2 z-50 -translate-x-1/2">
      <div
        className={`flex items-center gap-3 rounded-lg px-6 py-4 shadow-lg ${bg}`}
      >
        <div className={`rounded-full bg-white p-2`}>
          <Icon className={`size-5 ${iconColor}`} />
        </div>
        <p className="font-inter max-w-xs text-sm font-medium text-white">
          {toastMessage}
        </p>
        {toastExtraInfo && (
          <Button
            onClick={toastExtraInfo.action.onClick}
            variant="ghost"
            className="ml-2 text-white hover:text-gray-200"
          >
            {toastExtraInfo.action.label}
          </Button>
        )}
        <button
          onClick={hideToast}
          className="ml-2 text-white hover:text-gray-200"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
